import React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { IconButton } from '../ui/IconButton';
import { BellRing, Settings, LogOut, CheckCircle2 } from 'lucide-react';
import type { Notification } from '../../lib/notifications';
import { listNotifications, markNotificationRead, connectNotificationsSse, resolveNotificationPath, formatTimeAgo } from '../../lib/notifications';

export type BreadcrumbItem = { label: string; onClick?: () => void };

export const Topbar: React.FC<{ breadcrumbs?: BreadcrumbItem[]; onLogout?: () => void; onSettings?: () => void } & React.HTMLAttributes<HTMLDivElement>> = ({ breadcrumbs, onLogout, onSettings, ...rest }) => {
  const [notifOpen, setNotifOpen] = useState(false);
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const notifRef = useRef<HTMLDivElement | null>(null);
  const sseRef = useRef<{ close: () => void } | null>(null);

  const unread = useMemo(() => items.filter(i => !i.read).length, [items]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNotifOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  // Load initial notifications and connect SSE once
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const data = await listNotifications(50);
        if (mounted) setItems(data);
      } catch (_) {
        // ignore
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();

    const { close } = connectNotificationsSse(
      // default message handler: try to parse Notification shape
      (evt: MessageEvent) => {
        try {
          const payload = JSON.parse(evt.data || '{}');
          if (payload && payload.id && payload.title) {
            setItems(prev => [payload as Notification, ...prev].slice(0, 100));
          }
        } catch {}
      },
      {
        // Known custom events we may broadcast
        notify: (evt: MessageEvent) => {
          try {
            const payload = JSON.parse(evt.data || '{}');
            if (payload && payload.id && payload.title) {
              setItems(prev => [payload as Notification, ...prev].slice(0, 100));
            }
          } catch {}
        },
      }
    );
    sseRef.current = { close };
    return () => {
      mounted = false;
      sseRef.current?.close?.();
    };
  }, []);

  const onItemClick = async (n: Notification) => {
    try {
      if (!n.read) {
        // optimistic update
        setItems(prev => prev.map(x => (x.id === n.id ? { ...x, read: true } : x)));
        // server mark read
        markNotificationRead(n.id).catch(() => {
          // revert on failure
          setItems(prev => prev.map(x => (x.id === n.id ? { ...x, read: false } : x)));
        });
      }
    } catch {}
    const path = resolveNotificationPath(n);
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path);
    }
    // Signal route change to layout
    window.dispatchEvent(new PopStateEvent('popstate'));
    setNotifOpen(false);
  };

  return (
    <div className="sticky top-0 z-40 flex h-14 items-center justify-between border-b bg-background/60 px-4 backdrop-blur" {...rest}>
      <div className="flex min-w-0 items-center gap-2">
        <nav className="flex items-center gap-2 text-sm truncate" aria-label="Breadcrumb">
          {breadcrumbs && breadcrumbs.length > 0 ? (
            breadcrumbs.map((item, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && <span className="text-muted-foreground">/</span>}
                {item.onClick && idx < breadcrumbs.length - 1 ? (
                  <button className="truncate text-muted-foreground hover:text-foreground" onClick={item.onClick}>
                    {item.label}
                  </button>
                ) : (
                  <span className={idx === breadcrumbs.length - 1 ? 'truncate font-semibold' : 'truncate text-muted-foreground'}>
                    {item.label}
                  </span>
                )}
              </React.Fragment>
            ))
          ) : (
            <span className="font-semibold">Dashboard</span>
          )}
        </nav>
      </div>
      <div className="flex items-center gap-2">
        <div className="relative" ref={notifRef}>
          <IconButton title="Notifications" onClick={() => setNotifOpen(v => !v)} aria-expanded={notifOpen} aria-haspopup="menu">
            <div className="relative">
              <BellRing className="h-4 w-4" />
              {unread > 0 && (
                <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] leading-4 text-white">
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </div>
          </IconButton>
          {notifOpen && (
            <div role="menu" aria-label="Notifications" className="absolute right-0 mt-2 w-96 origin-top-right rounded-md border bg-popover text-popover-foreground shadow-md">
              <div className="flex items-center justify-between border-b px-3 py-2">
                <span className="text-sm font-semibold">Notifications ({unread}/{items.length})</span>
                <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setNotifOpen(false)} aria-label="Close notifications">Close</button>
              </div>
              <div className="max-h-96 overflow-auto p-1">
                {loading && items.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">Loading...</div>
                ) : items.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">You're all caught up. No new notifications.</div>
                ) : (
                  <ul className="divide-y">
                    {items.map(n => (
                      <li key={n.id}>
                        <button
                          onClick={() => onItemClick(n)}
                          className={`flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-accent ${n.read ? 'opacity-80' : ''}`}
                        >
                          <div className="mt-0.5">
                            {n.read ? (
                              <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <span className="inline-block h-2 w-2 rounded-full bg-blue-500" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <div className="truncate font-medium">{n.title}</div>
                              <div className="shrink-0 text-xs text-muted-foreground">{formatTimeAgo(n.createdAt)}</div>
                            </div>
                            {n.message && <div className="mt-0.5 truncate text-sm text-muted-foreground">{n.message}</div>}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
        <IconButton title="Settings" onClick={onSettings}>
          <Settings className="h-4 w-4" />
        </IconButton>
        <IconButton title="Logout" onClick={onLogout}>
          <LogOut className="h-4 w-4" />
        </IconButton>
      </div>
    </div>
  );
};

export default Topbar;
